import { useNavigate, useParams } from "react-router-dom";

import LayoutFullScreen from "@/ui/layouts/LayoutFullScreen";
import ErrorBoundary from "@/ui/organisms/ErrorBoundary";

import {
    ViewDatabaseGuard,
    ViewDealDetails,
    ViewTopBar,
    ViewNewExperienceSection,
    ViewNavigationSidebar,
} from "@/ui/compositionRootUI";

import { routerPaths, dealsDetailTabsValidator } from "@/ui/router/routerPaths";
import React from "react";
import ErrorMessage from "@/ui/atoms/ErrorMessage/ErrorMessage";
import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";

const PageDealDetail = () => {
    const navigate = useNavigate();
    const { projectId, databaseId, tab } = useParams() as {
        projectId: string;
        databaseId: string;
        tab: string;
    };

    const parsedProjectId = projectId ? parseInt(projectId, 10) : undefined;
    const parsedDatabaseId = databaseId ? parseInt(databaseId, 10) : undefined;
    const isValidIdTypeGuard = (value: unknown): value is number =>
        typeof value === "number" && !isNaN(value);

    React.useEffect(() => {
        const isValidTab = dealsDetailTabsValidator(tab);
        if (isValidTab) return;
        navigate(
            routerPaths.projects.detail({
                databaseId,
                projectId,
                tab: "properties",
            }),
        );
    }, [projectId, databaseId, navigate, tab]);

    const handleBack = () => {
        if (!projectId || !databaseId) {
            return;
        }
        return navigate(
            routerPaths.projects.read({
                databaseId,
                projectId,
                tab: "documents",
            }),
        );
    };
    const handleAddFileButtonClicked = () => {
        return navigate(routerPaths.files.upload.url());
    };

    return (
        <LayoutFullScreen
            TopBarProps={{
                sx: {
                    backgroundColor: "var(--proda-white-110)",
                },
                onLogoClick: () => navigate(routerPaths.main.default()),
            }}
            MainContentProps={{
                sx: {
                    backgroundColor: "var(--proda-white)",
                    borderTop: "1px solid var(--primary-color)",
                },
            }}
            contentTopBar={<ViewTopBar />}
            contentTopBarBanner={<ViewNewExperienceSection />}
            contentSidebar={
                <ViewNavigationSidebar
                    handleUploadFileButtonClicked={handleAddFileButtonClicked}
                />
            }
        >
            <ErrorBoundary>
                <ViewDatabaseGuard>
                    {!isValidIdTypeGuard(parsedProjectId) ||
                    !isValidIdTypeGuard(parsedDatabaseId) ? (
                        <LayoutFillParentCentered>
                            <ErrorMessage>
                                The provided project ID or database ID is
                                invalid. Please check the URL and try again.
                            </ErrorMessage>
                        </LayoutFillParentCentered>
                    ) : (
                        dealsDetailTabsValidator(tab) && (
                            <ViewDealDetails
                                activeDetailsTab={tab}
                                setActiveDetailsTab={targetTabName => {
                                    navigate(
                                        routerPaths.projects.detail({
                                            databaseId,
                                            projectId,
                                            tab: targetTabName,
                                        }),
                                    );
                                }}
                                projectId={parsedProjectId}
                                databaseId={parsedDatabaseId}
                                handleBackButtonClicked={handleBack}
                                sx={{
                                    margin: "1rem 0",
                                }}
                            />
                        )
                    )}
                </ViewDatabaseGuard>
            </ErrorBoundary>
        </LayoutFullScreen>
    );
};

export default PageDealDetail;
